import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Chart } from "react-google-charts"; // Import Chart component
import { getData } from "../../../CommonElements/Functions/PostGetFun"; // Import API call function
import { API } from "../../../api/API";
import "../Styles/leaves.css"; // Import CSS for styling

const Leaves: React.FC<{ employeeIdProp?: number }> = ({ employeeIdProp }) => {
  // State for calendar events
  const [events, setEvents] = useState<{ date: Date; type: string }[]>([]);
  // State for detailed leave data
  const [leaveDetails, setLeaveDetails] = useState<any[]>([]);
  const [leaveBalance, setLeaveBalance] = useState<any>(null); // State for leave balance data
  const [selectedDate, setSelectedDate] = useState<Date | null>(null); // State to track the selected date
  const serverUrl = localStorage.getItem("serverUrl");

  // Function to calculate the first and last date of a month
  const calculateMonthRange = (date: Date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDayOfYear = new Date(date.getFullYear(), 11, 31); // Last day of the year
    
    // Format date as YYYY-MM-DD in local timezone to avoid UTC conversion issues
    const formatDate = (d: Date) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      fromDate: formatDate(firstDay),
      toDate: formatDate(lastDayOfYear),
    };
  };

  // Fetch employee leave data
  const fetchEmployeeLeave = async (date: Date) => {
    if (!employeeIdProp) return;
    const serverUrl = localStorage.getItem("serverUrl");
    const url = `${serverUrl}${API.getEmployeeLeave}`;
    const { fromDate, toDate } = calculateMonthRange(date);

    try {
      const { data, status } = await getData(url, true, {
        employee_id: employeeIdProp,
        from_date: fromDate,
        to_date: toDate,
      });

      if (status === 200 && data?.data) {
        const leaveEvents: { date: Date; type: string }[] = [];
        const leaveDetailsData: any[] = [];

        // Process ApprovedLeave
        data.data.ApprovedLeave.forEach((leaveDate: string) => {
          leaveEvents.push({
            date: new Date(leaveDate),
            type: "Approved Leave",
          });
        });

        // Process Holiday
        data.data.Holiday.forEach((holidayDate: string) => {
          leaveEvents.push({ date: new Date(holidayDate), type: "Holiday" });
        });

        // Process WeekOff
        data.data.WeekOff.forEach((weekOffDate: string) => {
          leaveEvents.push({ date: new Date(weekOffDate), type: "Week Off" });
        });

        // Process LeaveRequest for detailed data
        if (data.data.LeaveRequest) {
          leaveDetailsData.push(...data.data.LeaveRequest);
          data.data.LeaveRequest.forEach(
            (leave: {
              LeaveFrom: string;
              LeaveTo: string;
              LeaveStatus: number;
            }) => {
              const leaveDates = getDatesBetweenTwoDates(
                leave.LeaveFrom,
                leave.LeaveTo
              );
              const leaveType =
                leave.LeaveStatus === 1 ? "Raised Leave" : "Approved Leave";
              leaveDates.forEach((date: string) => {
                leaveEvents.push({ date: new Date(date), type: leaveType });
              });
            }
          );
        }

        setEvents(leaveEvents); // Set calendar events
        setLeaveDetails(leaveDetailsData); // Set detailed leave data
      } else {
        console.error("Failed to fetch employee leave data");
      }
    } catch (error) {
      console.error("Error fetching employee leave data:", error);
    }
  };

  // Fetch leave balance data
  const fetchLeaveBalance = async (year: string) => {
    if (!employeeIdProp) return;
    const serverUrl = localStorage.getItem("serverUrl");
    const url = `${serverUrl}${API.getEmployeeLeaveBalance}`;

    try {
      const { data, status } = await getData(url, true, {
        employee_id: employeeIdProp,
        year,
      });

      if (status === 200 && data?.data) {
        setLeaveBalance(data?.data); // Set leave balance data
      } else {
        console.error("Failed to fetch leave balance data");
      }
    } catch (error) {
      console.error("Error fetching leave balance data:", error);
    }
  };

  // Fetch data when the component mounts (default to current month)
  useEffect(() => {
    const currentYear = new Date().getFullYear().toString();
    fetchEmployeeLeave(new Date());
    fetchLeaveBalance(currentYear);
  }, []);

  // Function to handle month change in the calendar
  const handleActiveStartDateChange = ({
    activeStartDate,
  }: {
    activeStartDate: Date | null;
  }) => {
    if (activeStartDate) {
      fetchEmployeeLeave(activeStartDate); // Only call the API if activeStartDate is not null
    }
  };

  // Utility function to get all dates between two dates
  const getDatesBetweenTwoDates = (
    startDate: string,
    endDate: string
  ): string[] => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const dates: string[] = [];

    while (start <= end) {
      dates.push(new Date(start).toISOString().split("T")[0]); // Add date in YYYY-MM-DD format
      start.setDate(start.getDate() + 1); // Increment the date by 1 day
    }

    return dates;
  };

  const isWeekOff = (date: Date) => {
    const normalizedDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    return events.some(
      (event) =>
        event.type === "Week Off" &&
        event.date.getFullYear() === normalizedDate.getFullYear() &&
        event.date.getMonth() === normalizedDate.getMonth() &&
        event.date.getDate() === normalizedDate.getDate()
    );
  };

  const tileClassName = ({ date }: { date: Date }) => {
    const normalizedDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    const event = events.find(
      (event) =>
        event.date.getFullYear() === normalizedDate.getFullYear() &&
        event.date.getMonth() === normalizedDate.getMonth() &&
        event.date.getDate() === normalizedDate.getDate()
    );

    if (isWeekOff(date)) {
      return "weekoff-tile";
    }

    return event
      ? `${event.type.toLowerCase().replace(" ", "-")}-tile`
      : "";
  };

  return (
    <div className="leaves-container px-2">
      <h3 className="leaves-title">Leaves Calendar</h3>
      <div className="leaves-content">
        {/* Left Section: Calendar and Legend */}
        <div className="calendar-and-legend">
          <div className="calendar-container">
            <Calendar
              onChange={(date) => setSelectedDate(date as Date)}
              value={selectedDate}
              tileClassName={tileClassName}
              onActiveStartDateChange={handleActiveStartDateChange} // Fetch data when the month changes
            />
          </div>
          <div className="legend mt-0">
            {/* <h4 className="legend-title">Legend</h4> */}
            <ul className="legend-list">
              <li className="legend-item" style={{ fontSize: "13px" }}>
                <span
                  className="legend-box holiday"
                  style={{ height: "12px", width: "12px" }}
                ></span>
                Holiday
              </li>
              <li className="legend-item" style={{ fontSize: "13px" }}>
                <span
                  className="legend-box approved-leave"
                  style={{ height: "12px", width: "12px" }}
                ></span>
                Approved
              </li>
              <li className="legend-item" style={{ fontSize: "13px" }}>
                <span
                  className="legend-box raised-leave"
                  style={{ height: "12px", width: "12px" }}
                ></span>
                Raised
              </li>
              <li className="legend-item" style={{ fontSize: "13px" }}>
                <span
                  className="legend-box weekoff"
                  style={{ height: "12px", width: "12px" }}
                ></span>
                Week Off
              </li>
              {/* <li className="legend-item" style={{ fontSize: "13px" }}>
                <span
                  className="legend-box sunday"
                  style={{ height: "12px", width: "12px" }}
                ></span>
                Sunday
              </li> */}
            </ul>
          </div>
        </div>

        {/* Right Section: Leave Details */}
        <div className="leave-details">
          <h4>Leave Details</h4>
          {leaveDetails.length > 0 ? (
            leaveDetails.map((leave) => (
              <div key={leave.LeaveReqId} className="card  mb-3 border ">
                <div className="card-header p-1 d-flex justify-content-between align-items-center">
                  <span className="fw-bold text-primary">
                    {leave.LeaveType}
                  </span>
                  <span
                    className={`badge ${
                      leave.LeaveStatus === 2
                        ? "bg-success"
                        : "bg-warning text-dark"
                    }`}
                  >
                    {leave.LeaveStatus === 2 ? "Approved" : "Raised"}
                  </span>
                </div>
                <div className="card-body p-1">
                  <div className="d-flex justify-content-between">
                    <p className="mb-2">
                      {new Date(leave.LeaveFrom).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}{" "}
                      to{" "}
                      {new Date(leave.LeaveTo).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </p>{" "}
                    <p className="mb-2">
                      <strong>{leave.LeaveDays}</strong> Full day
                    </p>
                  </div>
                  <p className="mb-0">
                    <strong>Reason:</strong>{" "}
                    {leave.LeaveReason || "No reason provided"}
                  </p>

                  {leave.LeaveRejectRemark && (
                    <p
                      style={{
                        fontSize: 14,
                        color: "#555",
                        lineHeight: "20px",
                      }}
                    >
                      <strong>Manager: </strong>
                      {leave.LeaveRejectRemark}
                    </p>
                  )}
                  {leave.Attachment && (
                    <a
                      href={`${serverUrl}media?id=${leave.Attachment}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "block",
                        color: "#007bff",
                        textAlign: "center",
                        textDecoration: "underline",
                        marginTop: "10px",
                      }}
                    >
                      View Attachment
                    </a>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="text-muted">
              No leave details available for this month.
            </p>
          )}
        </div>
      </div>
      <div className="leave-balance-container">
        <h4>Leave Balance</h4>
        {leaveBalance && (
          <div className="leave-balance-row">
            {Object.keys(leaveBalance).map((key) => {
              const leave = leaveBalance[key];
              const allZero =
                leave.Opening === 0 &&
                leave.Consumed === 0 &&
                leave.Balance === 0 &&
                leave.InProgress === 0;

              const chartData = [
                ["Category", "Value"],
                ["Opening", Math.abs(leave.Opening)], // Use absolute value
                ["Consumed", Math.abs(leave.Consumed)], // Use absolute value
                ["Balance", Math.abs(leave.Balance)], // Use absolute value
                ["In Progress", Math.abs(leave.InProgress)], // Use absolute value
              ];

              return (
                <div key={key} className="leave-chart-container">
                  <div className="card ">
                    <div
                      style={{ padding: "0px", paddingTop: "5px" }}
                      className="card-header text-center fw-bold"
                    >
                      {leave.LeaveType}
                    </div>
                    <div className="card-body" style={{ padding: "0px" }}>
                      <Chart
                        chartType="PieChart"
                        data={
                          allZero
                            ? [
                                ["Category", "Value"],
                                ["No Data", 1],
                              ]
                            : chartData
                        }
                        options={{
                          pieHole: 0.6,
                          legend: {
                            position: "bottom",
                          },
                          pieSliceText: "none",
                          slices: allZero
                            ? { 0: { color: "#d3d3d3" } } // Grey color for no data
                            : {
                                0: { color: "#338bff" }, // Opening
                                1: { color: "#1c65d3" }, // Consumed
                                2: { color: "#0800fc" }, // Balance
                                3: { color: "#85cffd" }, // In Progress
                              },
                        }}
                        height="150px" // Chart height
                        width="170px" // Chart width
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaves;
